import { LockIcon } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { Button } from 'components/ui/button';
import { Popover, PopoverContent, PopoverTitle, PopoverTrigger } from 'components/ui/popover';
import type { WidgetLock } from 'types/homeWidgetSharing';
import { t } from 'utils/getTranslations';

type Props = {
    /** The widget's name, which tells one lock from the next for a screen reader. */
    name: string;
    lock: WidgetLock;
};

/**
 * The end of the header of a widget the firm put on a Home, where an own widget has its menu: a lock that says who
 * put it there, for whom and since when. `for` holds names alone, so an office, a team and the viewer's own name
 * read alike.
 */
export function LockPanel({ name, lock }: Props) {
    const audience = lock.everyone ? t('home.widgets.share.everyone') : lock.for.join(', ');
    return (
        <Popover>
            <PopoverTrigger
                render={
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('home.widgets.locked.label', { name })}
                        data-tour="home-widget-lock"
                        className="text-muted-foreground -me-1.5 size-7"
                    />
                }
            >
                <LockIcon className="size-3.5" />
            </PopoverTrigger>
            <PopoverContent align="end" className="text-meta w-[312px] gap-2 rounded-[10px] px-3.5 py-3">
                <PopoverTitle className="flex items-center gap-2 font-semibold">
                    <LockIcon className="size-3.5" />
                    {t('home.widgets.locked.title')}
                </PopoverTitle>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-3 gap-y-1">
                    <dt className="text-muted-foreground">{t('home.widgets.locked.by')}</dt>
                    <dd>{lock.by.name}</dd>
                    {audience ? (
                        <>
                            <dt className="text-muted-foreground">{t('home.widgets.locked.for')}</dt>
                            <dd>{audience}</dd>
                        </>
                    ) : null}
                    <dt className="text-muted-foreground">{t('home.widgets.locked.since')}</dt>
                    <dd>
                        <FormattedDate value={lock.since} />
                    </dd>
                </dl>
                <p className="border-neutral-tint text-foreground-secondary border-t pt-2">
                    {t('home.widgets.locked.note')}
                </p>
            </PopoverContent>
        </Popover>
    );
}
